import { useCallback, useEffect, useMemo, useState } from 'react';
import { FiTruck, FiHeart } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { useCurrency } from '../../context/CurrencyContext';
import { apiEstimateShipping } from '../../api/shipping';
import { apiGetCountries } from '../../api/geo';
import { formatReleaseDate, isPreorderActive } from '../../utils/preorder';
import { getPurchaseIssue } from '../../utils/stock';
import { getReturnWindowDays } from '../../utils/returnPolicy';

// Temporarily disabled; checkout still calculates delivery charges.
const PRODUCT_DELIVERY_ESTIMATOR_ENABLED = false;

const getPostalConfig = (countryConfig) => {
  let pattern;
  try {
    pattern = new RegExp(
      countryConfig?.postalCodePattern || '^[A-Z0-9][A-Z0-9 -]{0,10}[A-Z0-9]$'
    );
  } catch {
    pattern = /^[A-Z0-9][A-Z0-9 -]{0,10}[A-Z0-9]$/;
  }
  const maxLength = Number(countryConfig?.postalCodeMaxLength) || 12;
  return {
    isValid: (value) => pattern.test(value),
    sanitize: (value) => value.toUpperCase().replace(/[^A-Z0-9 -]/g, '').slice(0, maxLength),
    placeholder: countryConfig?.postalCodePlaceholder || 'Postal code',
    label: `Delivery postal code for ${countryConfig?.name || 'selected country'}`,
    inputMode: 'text',
  };
};

const BuyBox = ({
  book,
  qty,
  onQtyChange,
  isOutOfStock,
  inWishlist,
  onAddToCart,
  onBuyNow,
  onWishlist,
}) => {
  const { convertFrom, formatCurrencyAmount, formatBookPrice, getBookAmount } = useCurrency();
  const purchaseIssue = getPurchaseIssue({ ...book, quantity: qty }, getBookAmount(book));
  const maxQty = Math.min(10, Math.max(1, book.stock || 1));
  const [country, setCountry] = useState(() => localStorage.getItem('bookscape_country_v2') || '');
  const [countryConfig, setCountryConfig] = useState(null);
  // Delivery codes are intentionally session/page-local. A refresh must start
  // with an empty field instead of reusing a previous destination.
  const [pincode, setPincode] = useState('');
  const [shipping, setShipping] = useState(null);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [shippingError, setShippingError] = useState('');
  const postalConfig = useMemo(() => getPostalConfig(countryConfig), [countryConfig]);
  const returnWindowDays = getReturnWindowDays(book, country);
  const isPreorder = isPreorderActive(book);

  useEffect(() => {
    let cancelled = false;
    apiGetCountries()
      .then((data) => {
        if (cancelled || !Array.isArray(data?.countries)) return;
        const selectedCode = localStorage.getItem('bookscape_country_v2') || 'IN';
        const selectedConfig = data.countries.find(
          ({ code }) => String(code).toUpperCase() === selectedCode.toUpperCase()
        );
        if (selectedConfig) {
          setCountry(selectedConfig.code);
          setCountryConfig(selectedConfig);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const loadShipping = useCallback(async (destinationCountry, destinationPincode) => {
    if (!PRODUCT_DELIVERY_ESTIMATOR_ENABLED) return;
    const config = getPostalConfig(countryConfig);
    if (!config.isValid(destinationPincode)) {
      setShipping(null);
      return;
    }
    setShippingLoading(true);
    setShippingError('');
    try {
      const result = await apiEstimateShipping({
        country: destinationCountry,
        pincode: destinationPincode,
        amount: Number(book.price) * qty,
        itemCount: qty,
      });
      setShipping(result.reason ? null : result);
      if (result.reason) setShippingError('Shipping is not configured for this destination.');
    } catch (error) {
      setShipping(null);
      setShippingError(error.message || 'Unable to calculate shipping.');
    } finally {
      setShippingLoading(false);
    }
  }, [book.price, qty, countryConfig]);

  useEffect(() => {
    const handleCountryChange = (event) => {
      const nextConfig = event.detail && typeof event.detail === 'object'
        ? event.detail
        : { code: event.detail || '' };
      setCountry(nextConfig.code);
      setCountryConfig(nextConfig);
      setPincode('');
      setShipping(null);
      setShippingError('');
    };
    window.addEventListener('bookscape:country-change', handleCountryChange);
    return () => window.removeEventListener('bookscape:country-change', handleCountryChange);
  }, []);

  useEffect(() => {
    if (!PRODUCT_DELIVERY_ESTIMATOR_ENABLED) return;
    if (postalConfig.isValid(pincode)) {
      loadShipping(country, pincode);
    } else {
      setShipping(null);
    }
  }, [country, pincode, loadShipping, postalConfig]);

  const handlePincodeChange = (event) => {
    const value = postalConfig.sanitize(event.target.value);
    setPincode(value);
    setShipping(null);
    setShippingError('');
  };

  return (
    <aside className="w-full border border-gray-200 rounded-lg p-5 h-fit bg-white shadow-sm md:col-span-2 lg:col-span-1">
      <p className="text-2xl font-medium text-black">{formatBookPrice(book)}</p>
      {book.taxLabel ? (
        <p className="text-sm text-black">{book.taxLabel}</p>
      ) : book.taxRate != null ? (
        <p className="text-sm text-black">
          {book.taxInclusive ? 'Tax included' : 'Tax calculated at checkout'} ({Number(book.taxRate)}%)
        </p>
      ) : null}

      {(PRODUCT_DELIVERY_ESTIMATOR_ENABLED || isPreorder) && <div className="mt-4">
        {PRODUCT_DELIVERY_ESTIMATOR_ENABLED && <>
        <label className="flex items-center gap-2 text-sm text-black">
          <span className="shrink-0">Deliver to</span>
          <input
            type="text"
            inputMode={postalConfig.inputMode}
            value={pincode}
            onChange={handlePincodeChange}
            placeholder={postalConfig.placeholder}
            aria-label={postalConfig.label}
            autoComplete="postal-code"
            className="min-w-0 flex-1 border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:border-orange-500"
          />
        </label>
        {shippingLoading && <p className="mt-2 text-xs text-black">Calculating shipping...</p>}
        </>}
        {isPreorder ? (
          <p className="mt-2 text-sm font-medium text-violet-700">
            Expected to ship on or after{' '}
            <span className="font-bold text-orange-600">
              {formatReleaseDate(book.preorderReleaseDate)}
            </span>
          </p>
        ) : PRODUCT_DELIVERY_ESTIMATOR_ENABLED && shipping && !shippingLoading && (
          <p className="text-base mt-2">
            <FiTruck className="inline mr-1 text-black" />
            <span className="text-black">
              {shipping.freeApplied
                ? 'FREE delivery'
                : `Delivery ${formatCurrencyAmount(convertFrom(shipping.shipping, shipping.currencyCode || 'INR'))}`}{' '}
            </span>
            {shipping.deliveryEstimate && (
              <strong className="text-black">in {shipping.deliveryEstimate}</strong>
            )}
          </p>
        )}
        {PRODUCT_DELIVERY_ESTIMATOR_ENABLED && shippingError && <p className="mt-2 text-xs text-red-600">{shippingError}</p>}
      </div>}

      {isOutOfStock ? (
        <p className="text-orange-600 text-base font-medium mt-2">Coming soon</p>
      ) : isPreorder ? (
        <div className="mt-3 rounded-lg border border-violet-200 bg-violet-50 p-3">
          <p className="text-lg font-semibold text-violet-800">Available for pre-order</p>
          <p className="mt-1 text-xs text-violet-700">
            {book.preorderMessage || 'This item is expected to release on the date shown above.'}
          </p>
        </div>
      ) : (
        <p className="text-green-700 text-xl font-medium mt-3">In stock</p>
      )}

      <label className="flex items-center gap-2 mt-3 text-xs text-black">
        <span>Quantity:</span>
        <select
          value={qty}
          onChange={(e) => onQtyChange(Number.parseInt(e.target.value, 10))}
          className="border border-gray-300 rounded px-2 py-1 text-xs bg-gray-50"
          disabled={isOutOfStock || Boolean(purchaseIssue)}
        >
          {Array.from({ length: maxQty }, (_, i) => i + 1).map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </label>

      <button
        type="button"
        onClick={onAddToCart}
        disabled={isOutOfStock || Boolean(purchaseIssue)}
        className="preorder-cart-button mt-4 w-full rounded-full border border-orange-600 py-2.5 text-base font-semibold text-white shadow-sm transition focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-black"
      >
        {isPreorder ? 'Pre-order — Add to Cart' : 'Add to Cart'}
      </button>
      {purchaseIssue === 'Price currently unavailable' && <p role="status" className="mt-2 text-sm text-amber-700">The price for this book is currently unavailable. Please check again later.</p>}
      <button
        type="button"
        onClick={onBuyNow}
        disabled={isOutOfStock || Boolean(purchaseIssue)}
        className="w-full mt-2 bg-orange-500 hover:bg-orange-600 text-white font-medium text-base py-2.5 rounded-full border border-orange-600 shadow-sm disabled:bg-gray-200 disabled:cursor-not-allowed disabled:text-black"
      >
        {isPreorder ? 'Pre-order Now' : 'Buy Now'}
      </button>

      <dl className="text-xs mt-3 space-y-1">
        <div className="flex justify-between">
          <dt className="text-black">Returns</dt>
          <dd className="text-black">
            {returnWindowDays > 0
              ? `${returnWindowDays} days from delivery`
              : 'Non-returnable'}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt>
            <Link to="/payment" className="text-black hover:text-orange-600 hover:underline">
              Payment
            </Link>
          </dt>
          <dd>
            <Link to="/payment" className="text-blue-700 hover:text-blue-800 hover:underline">
              Secure transaction
            </Link>
          </dd>
        </div>
      </dl>

      <button
        type="button"
        onClick={onWishlist}
        className={`w-full mt-4 border text-sm py-2 rounded-full flex items-center justify-center gap-2 ${
          inWishlist
            ? 'bg-rose-50 border-rose-300 text-rose-600'
            : 'bg-white border-gray-300 text-black hover:bg-gray-50'
        }`}
      >
        <FiHeart className={inWishlist ? 'fill-rose-500' : ''} />
        {inWishlist ? 'In Wish List' : 'Add to Wish List'}
      </button>
    </aside>
  );
};

export default BuyBox;
